/**
 * shopifyFormatMoney
 *
 * Formats an amount in cents according to Shopify's money template tokens.
 * Based on https://gist.github.com/stewartknapman/8d8733ea58d2314c373e94114472d44c
 *
 * Supported tokens:
 *   - {{amount}}                                      → 1,234.56
 *   - {{amount_no_decimals}}                          → 1,235
 *   - {{amount_with_comma_separator}}                 → 1.234,56
 *   - {{amount_no_decimals_with_comma_separator}}     → 1.235
 *   - {{amount_with_apostrophe_separator}}            → 1'234.56
 *   - {{amount_no_decimals_with_apostrophe_separator}} → 1'235
 *   - {{amount_with_space_separator}}                 → 1 234,56
 *   - {{amount_no_decimals_with_space_separator}}     → 1 235
 *   - {{amount_no_trailing_zeros}}                    → 1,234.5 or 1,234
 */

export default function shopifyFormatMoney(
  cents: number,
  format?: string,
): string {
  // Default to include currency symbol if no format provided
  const formatString = format || '${{amount}}';

  function formatWithDelimiters(
    amtInCents: number,
    precision = 2,
    thousands = ',',
    decimal = '.',
  ): string {
    const amount = amtInCents / 100;

    // Handle precision
    let formatted: string;
    if (precision === -1) {
      // No trailing zeros
      formatted = amount.toFixed(2);
      // Remove trailing zeros after decimal point
      formatted = formatted.replace(/\.?0+$/, '');
    } else {
      formatted = amount.toFixed(precision);
    }

    const parts = formatted.split('.');
    const intPart = parts[0];
    const decPart = parts[1];

    // Add thousand separators
    const withThousands = intPart.replace(
      /(\d)(?=(\d{3})+(?!\d))/g,
      `$1${thousands}`,
    );

    // Reconstruct the number
    return withThousands + (decPart ? decimal + decPart : '');
  }

  // Map of all supported Shopify money format tokens
  const tokenFormatters: Record<string, () => string> = {
    amount: () => formatWithDelimiters(cents, 2, ',', '.'),
    amount_no_decimals: () => formatWithDelimiters(cents, 0, ',', '.'),
    amount_with_comma_separator: () => formatWithDelimiters(cents, 2, '.', ','),
    amount_no_decimals_with_comma_separator: () =>
      formatWithDelimiters(cents, 0, '.', ','),
    amount_with_apostrophe_separator: () =>
      formatWithDelimiters(cents, 2, "'", '.'),
    amount_no_decimals_with_apostrophe_separator: () =>
      formatWithDelimiters(cents, 0, "'", '.'),
    amount_with_space_separator: () => formatWithDelimiters(cents, 2, ' ', ','),
    amount_no_decimals_with_space_separator: () =>
      formatWithDelimiters(cents, 0, ' ', ','),
    amount_no_trailing_zeros: () => formatWithDelimiters(cents, -1, ',', '.'),
  };

  // Replace all placeholders in the format string
  let result = formatString;
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/g;
  const unknownTokens = new Set<string>();

  result = result.replace(placeholderRegex, (match, token) => {
    const formatter = tokenFormatters[token];
    if (formatter) {
      const formattedMoney = formatter();
      return formattedMoney;
    } else {
      unknownTokens.add(token);
      return match;
    }
  });

  return result;
}

// Helper function to validate if a format string would produce a valid result
export function isValidShopifyMoneyFormat(format: string): boolean {
  if (!format || !format.trim()) return false;

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/g;
  const matches = format.match(placeholderRegex);

  // No placeholders found
  if (!matches || matches.length === 0) return false;

  // Check if there's any content outside of placeholders (currency symbol, text, etc.)
  const withoutPlaceholders = format.replace(placeholderRegex, '');
  if (!withoutPlaceholders.trim()) return false;

  return true;
}
